import { useState } from 'react'

import { Box, Divider, Menu, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { AlertTriangle, Plus } from 'lucide-react'
import type { EmployerContactType, EmploymentExperience } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'

import StyledAlert from 'src/components/Feedback/StyledAlert/StyledAlert'
import ContactCountBadge, {
  getContactBadge,
  type ContactBadge,
} from 'src/components/VoE/ContactCountBadge/ContactCountBadge'
import { useEmploymentDetailsDrawerContext } from 'src/context/EmploymentDetailsDrawerContext'
import {
  formatEmploymentEquipmentType,
  sortEquipmentTypes,
} from 'src/graphql/types/employmentExperiences'
import { calculateAverageJobDuration } from 'src/lib/employment.utils'
import {
  calculateEmploymentGaps,
  checkForSignificantGaps,
  toEmploymentDate,
  type EmploymentPeriod,
} from 'src/lib/employmentGapCalculator'
import { formatPhone } from 'src/lib/phone.utils'
import { knownPlatformLabel } from 'src/lib/voeProvider.utils'
import { DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS } from 'src/lib/voeVerificationPeriod'

import { getUtcMonth } from '../../../utils'

dayjs.extend(utc)

// Grid column style shared across all row types
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 130px 130px 70px',
  alignItems: 'center',
  gap: '8px',
}

/**
 * Helper function to safely create dayjs objects from potentially invalid dates
 */
const safeDayjs = (date: string | null | undefined, fallback?: dayjs.Dayjs) => {
  if (!date || date === '') {
    return fallback || dayjs()
  }
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed : fallback || dayjs()
}

/**
 * Helper function to check if company details exist and are not empty
 */
const hasCompanyDetails = (item: EmploymentExperienceRow) => {
  const hasContactName = item?.contactName && item.contactName.trim() !== ''
  const hasAddress =
    item?.address &&
    (item.address.street ||
      item.address.city ||
      item.address.state ||
      item.address.zipCode)
  const hasEmail = item?.email && item.email.trim() !== ''
  const hasPhone = item?.phone && item.phone.trim() !== ''

  return hasContactName || hasAddress || hasEmail || hasPhone
}

/**
 * Helper function to format address for display
 */
const formatAddress = (address: EmploymentExperienceRow['address']) => {
  if (!address) return ''
  const parts = [
    address.street,
    address.city,
    address.state,
    address.zipCode,
  ].filter(Boolean)
  return parts.join(', ')
}

type VoeStatus =
  | 'Completed'
  | 'In Progress'
  | 'Requested'
  | 'Not Started'
  | 'Canceled'
  | 'Declined'

type EmploymentExperienceRow = Omit<
  EmploymentExperience,
  'carrierContactCount' | 'suppressedContactTypes' | 'carrierRouting'
> & {
  voeStatus?: VoeStatus
  hasConsent?: boolean
  // Optional here so rows built from older selections or fixtures still render:
  // a missing count reads as 0, missing opt-outs as none.
  carrierContactCount?: number | null
  suppressedContactTypes?: EmployerContactType[] | null
  carrierRouting?: {
    preferredPlatform?: string | null
    platformOnly: boolean
  } | null
}

// The badge and its rule live with the VoE screens too; re-exported so the
// table's tests keep their import.
export { getContactBadge }
export type { ContactBadge }

const voeTooltips: Record<VoeStatus, string> = {
  Completed: 'Employment verification has been completed for this employer',
  'In Progress': 'Employment verification is currently being processed',
  Requested: 'Employment verification has been requested from this employer',
  'Not Started': 'Employment verification has not been initiated yet',
  Canceled: 'Employment verification was canceled for this employer',
  Declined: 'Employer declined the employment verification request',
}

const VoeBadge = ({
  status,
  onClick,
}: {
  status: VoeStatus
  onClick?: () => void
}) => {
  const styles: Record<VoeStatus, string> = {
    Completed: 'bg-green-50 text-green-700',
    'In Progress': 'bg-blue-50 text-blue-700',
    Requested: 'bg-blue-50 text-blue-700',
    'Not Started': 'bg-gray-100 text-gray-500',
    Canceled: 'bg-red-50 text-red-700',
    Declined: 'bg-red-50 text-red-700',
  }
  const className = `inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
    styles[status] || 'bg-gray-100 text-gray-500'
  }`
  return (
    <Tooltip label={voeTooltips[status]} position="top" withArrow>
      {onClick ? (
        <button
          type="button"
          className={`${className} hover:brightness-95`}
          onClick={(e) => {
            // The row opens the employment drawer; the badge opens the VoE.
            e.stopPropagation()
            onClick()
          }}
        >
          {status}
        </button>
      ) : (
        <span className={className}>{status}</span>
      )}
    </Tooltip>
  )
}

/**
 * How the employer answers verifications, as the VoE tab shows it: the
 * carrier's preferred platform. A platform-only carrier always gets the badge
 * ("Platform only" when it lists no platform), and its row says nothing about
 * contacts, since outreach never reaches them.
 */
const PlatformBadge = ({
  routing,
}: {
  routing?: EmploymentExperienceRow['carrierRouting']
}) => {
  const label = knownPlatformLabel(routing?.preferredPlatform)
  const platformOnly = Boolean(routing?.platformOnly)
  if (!label && !platformOnly) return null

  const tooltip = !platformOnly
    ? `This employer answers verifications through ${label}`
    : label
    ? `This employer answers verifications only through ${label}`
    : 'This employer answers verifications only through a verification platform'
  return (
    <Tooltip label={tooltip} position="top" withArrow>
      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
        {label ?? 'Platform only'}
      </span>
    </Tooltip>
  )
}

/**
 * Contact details row shown on hover (Row 3)
 * Structured mini-grid with labeled fields instead of plain text
 */
const ContactDetailsRow = ({ item }: { item: EmploymentExperienceRow }) => {
  if (!hasCompanyDetails(item)) return null

  const phone = formatPhone(item?.phone)
  const address = formatAddress(item?.address)

  const fields: { label: string; value: string }[] = []
  if (item?.contactName?.trim())
    fields.push({ label: 'Contact', value: item.contactName.trim() })
  if (item?.email?.trim())
    fields.push({ label: 'Email', value: item.email.trim() })
  if (phone) fields.push({ label: 'Phone', value: phone })
  if (address) fields.push({ label: 'Address', value: address })

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dashed border-doubleNickel-gray-200 pt-1.5">
      {fields.map((f, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-doubleNickel-gray-500">
            {f.label}
          </span>
          <span className="text-xs text-doubleNickel-gray-700">{f.value}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Employment entry (non-unemployment)
 */
const EmploymentSummaryInfo = ({
  item,
  onOpenVoe,
}: {
  item: EmploymentExperienceRow
  onOpenVoe?: (voeId: string) => void
}) => {
  const [isHovering, setIsHovering] = useState(false)

  const equipmentPills = item?.equipmentTypes
    ? sortEquipmentTypes(item.equipmentTypes)
        .map((t) => formatEmploymentEquipmentType(t))
        .filter(Boolean)
    : []

  return (
    <div
      className="flex-1 rounded p-3 hover:cursor-pointer hover:bg-doubleNickel-gray-50"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Row 1 */}
      <div style={gridStyle}>
        {/* Col 1: company + position */}
        <div className="flex min-w-0 items-baseline gap-2">
          <span
            className="truncate text-doubleNickel-gray-900"
            style={{ fontSize: 13, fontWeight: 600 }}
            title={item.companyName}
          >
            {item.companyName}
          </span>
          <span className="whitespace-nowrap text-xs text-doubleNickel-gray-700">
            {item.position}
          </span>
        </div>

        {/* Col 2: warning badges (No DOT), directory contacts badge, platform */}
        <div className="flex items-center gap-1.5 justify-self-end">
          {!item.dotNumber && (
            <Tooltip
              label="This employer has no DOT number — may not be a trucking-related position"
              position="top"
              withArrow
            >
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                No DOT
              </span>
            </Tooltip>
          )}
          {!item.carrierRouting?.platformOnly && (
            <ContactCountBadge item={item} hideWhenNone />
          )}
          <PlatformBadge routing={item.carrierRouting} />
        </div>

        {/* Col 3: VoE badge OR Consent Not Signed (VoEs are only created when the driver signs, so no VoE means no signed consent for this employer) */}
        <div className="flex items-center justify-center">
          {item.voeStatus ? (
            <VoeBadge
              status={item.voeStatus}
              onClick={
                onOpenVoe && item.verificationOfEmployment?.voeId
                  ? () => onOpenVoe(item.verificationOfEmployment.voeId)
                  : undefined
              }
            />
          ) : item.hasConsent === false ? (
            <Tooltip
              label="The driver hasn't signed a verification consent covering this employer, so we can't contact them yet. Ask the driver to re-sign their application."
              position="top"
              withArrow
              multiline
              w={280}
            >
              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                Consent Not Signed
              </span>
            </Tooltip>
          ) : null}
        </div>

        {/* Col 4: date range */}
        <div
          className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {getUtcMonth(item.startDate, 'N/A')} &ndash;{' '}
          {getUtcMonth(item.endDate, 'Current')}
        </div>

        {/* Col 5: duration */}
        <div
          className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {calculateAverageJobDuration([item])}
        </div>
      </div>

      {/* Row 2: reason left, equipment pills right */}
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className="flex-shrink-0 truncate text-xs text-doubleNickel-gray-700"
          style={{ maxWidth: 200 }}
        >
          {item?.reasonForLeaving || 'N/A'}
        </span>
        {equipmentPills.length > 0 && (
          <div className="ml-auto flex flex-wrap justify-end gap-1">
            {equipmentPills.map((label, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-gray-600"
                style={{ fontSize: 10 }}
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Row 3: contact details on hover */}
      {isHovering && <ContactDetailsRow item={item} />}
    </div>
  )
}

/**
 * Unemployment entry
 */
const UnemploymentSummaryInfo = ({
  item,
}: {
  item: EmploymentExperienceRow
}) => (
  <div className="flex-1 rounded bg-doubleNickel-gray-50 p-3 hover:cursor-pointer">
    <div style={gridStyle}>
      {/* Col 1: label + reason */}
      <div className="flex min-w-0 items-baseline gap-2">
        <span
          className="text-doubleNickel-gray-900"
          style={{ fontSize: 13, fontWeight: 600 }}
        >
          Unemployed
        </span>
        {item?.unemploymentReason && (
          <span className="truncate text-xs text-doubleNickel-gray-700">
            {item.unemploymentReason}
          </span>
        )}
      </div>

      {/* Col 2: empty */}
      <div />

      {/* Col 3: empty */}
      <div />

      {/* Col 4: date range */}
      <div
        className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
        style={{ fontFamily: 'monospace' }}
      >
        {getUtcMonth(item.startDate, 'N/A')} &ndash;{' '}
        {getUtcMonth(item.endDate, 'Current')}
      </div>

      {/* Col 5: duration */}
      <div
        className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
        style={{ fontFamily: 'monospace' }}
      >
        {calculateAverageJobDuration([item])}
      </div>
    </div>
  </div>
)

/**
 * Employment gap row (warning state)
 */
const EmploymentGapRow = ({
  startDate,
  endDate,
  duration,
  durationString,
  onAddEmployment,
  onAddUnemployment,
}: {
  startDate?: Date | null
  endDate?: Date | null
  duration?: number
  durationString?: string
  onAddEmployment?: (startDate?: string, endDate?: string) => void
  onAddUnemployment?: (startDate?: string, endDate?: string) => void
}) => {
  return (
    <div className="rounded border-l-2 border-amber-300 bg-amber-50 p-3">
      <div style={gridStyle}>
        {/* Col 1: warning label */}
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={14} className="text-amber-500" />
          <span className="font-medium text-amber-700" style={{ fontSize: 13 }}>
            Employment Gap
          </span>
        </div>

        {/* Col 2: empty */}
        <div />

        {/* Col 3: add employment/unemployment button */}
        <div className="flex items-center justify-center">
          {(onAddEmployment || onAddUnemployment) && (
            <Menu shadow="md" position="bottom" withinPortal>
              <Menu.Target>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-200"
                >
                  <Plus size={10} />
                  Add
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                {onAddEmployment && (
                  <Menu.Item
                    className="text-doubleNickel-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAddEmployment(
                        startDate?.toISOString(),
                        endDate?.toISOString()
                      )
                    }}
                  >
                    Add employment
                  </Menu.Item>
                )}
                {onAddUnemployment && (
                  <Menu.Item
                    className="text-doubleNickel-gray-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAddUnemployment(
                        startDate?.toISOString(),
                        endDate?.toISOString()
                      )
                    }}
                  >
                    Add unemployment
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </div>

        {/* Col 4: date range */}
        <div
          className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {getUtcMonth(startDate, 'N/A')} &ndash;{' '}
          {getUtcMonth(endDate, 'Current')}
        </div>

        {/* Col 5: duration */}
        <div
          className="whitespace-nowrap text-right text-xs text-doubleNickel-gray-700"
          style={{ fontFamily: 'monospace' }}
        >
          {durationString || ''}
        </div>
      </div>
    </div>
  )
}

const EmploymentTable = ({
  experiences,
  setCurrentIndex,
  applicationId,
  applicantId,
  verificationPeriodMonths = DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  onAddEmployment,
  onAddUnemployment,
}: {
  experiences: EmploymentExperienceRow[]
  handleDelete: (index: string) => void
  currentIndex: number
  setCurrentIndex: (index: number) => void
  applicationId: string
  applicantId?: string
  verificationPeriodMonths?: number
  onAddEmployment?: (startDate?: string, endDate?: string) => void
  onAddUnemployment?: (startDate?: string, endDate?: string) => void
}) => {
  const { handleOpen: openEmploymentDetailsDrawer } =
    useEmploymentDetailsDrawerContext()

  // The VoE drawer belongs to the VoE tab; it opens the VoE named in the URL.
  const openVoe = applicantId
    ? (voeId: string) =>
        navigate(
          routes.applicantDetails({ id: applicantId, tab: 'voe', voeId })
        )
    : undefined

  const experiencePeriods: EmploymentPeriod[] = experiences.flatMap((exp) => {
    const startDate = toEmploymentDate(exp.startDate)
    if (!startDate) return []

    return [
      {
        startDate,
        endDate: toEmploymentDate(exp.endDate),
        isUnemployment: exp.isUnemployment,
      },
    ]
  })

  // Use the gap calculation logic
  const { startGap, endGap } = checkForSignificantGaps(experiencePeriods)
  const gaps = calculateEmploymentGaps(experiencePeriods)

  const currentDate = dayjs.utc().startOf('month')
  const verificationCutoff = dayjs
    .utc()
    .subtract(verificationPeriodMonths, 'month')

  // Find the latest end date across all experiences for the start-gap display.
  // We can't just use experiences[0] because that's sorted by startDate desc —
  // a job that started earlier but is still current (endDate null) may cover
  // the period up to today.
  const latestEndDate = startGap
    ? experiences.reduce((latest, exp) => {
        const expEnd = exp.endDate ? dayjs.utc(exp.endDate) : currentDate
        return expEnd.isAfter(latest) ? expEnd : latest
      }, dayjs.utc(0))
    : null

  const startGapStartDateRaw = latestEndDate
    ? latestEndDate.add(1, 'month').startOf('month')
    : null
  const startGapStartDate =
    startGap && startGapStartDateRaw
      ? (startGapStartDateRaw.isAfter(currentDate)
          ? currentDate
          : startGapStartDateRaw
        ).toDate()
      : null
  const startGapEndDate = startGap ? currentDate.toDate() : null
  const startGapDuration =
    startGap && startGapStartDate && startGapEndDate
      ? Math.max(
          0,
          Math.round(
            currentDate.diff(dayjs.utc(startGapStartDate), 'month', true)
          )
        )
      : 0
  const startGapDurationString = startGap
    ? startGapDuration >= 12
      ? (startGapDuration / 12).toFixed(1) + ' years'
      : startGapDuration < 1
      ? '< 1 month'
      : startGapDuration === 1
      ? '1 month'
      : Math.round(startGapDuration) + ' months'
    : ''

  // endGap: gap between oldest job's start and verification cutoff
  const endGapStartDate = endGap ? verificationCutoff.toDate() : null
  const earliestStart = endGap
    ? experiences.reduce((earliest, exp) => {
        if (!exp.startDate) return earliest
        const expStart = dayjs.utc(exp.startDate)
        return expStart.isBefore(earliest) ? expStart : earliest
      }, dayjs.utc())
    : null
  const endGapEndDate =
    endGap && earliestStart
      ? earliestStart.subtract(1, 'month').endOf('month').toDate()
      : null
  const endGapDuration =
    endGap && endGapStartDate && endGapEndDate
      ? Math.max(
          0,
          Math.round(
            dayjs
              .utc(endGapEndDate)
              .diff(dayjs.utc(endGapStartDate), 'month', true)
          )
        )
      : 0
  const endGapDurationString = endGap
    ? endGapDuration >= 12
      ? (endGapDuration / 12).toFixed(1) + ' years'
      : endGapDuration < 1
      ? '< 1 month'
      : endGapDuration === 1
      ? '1 month'
      : Math.round(endGapDuration) + ' months'
    : ''

  return (
    <div className="flex flex-row">
      <div className="flex flex-1 flex-col overflow-hidden">
        {experiences.length > 0 ? (
          <>
            {startGap && (
              <>
                <EmploymentGapRow
                  startDate={startGapStartDate}
                  endDate={startGapEndDate}
                  duration={startGapDuration}
                  durationString={startGapDurationString}
                  onAddEmployment={onAddEmployment}
                  onAddUnemployment={onAddUnemployment}
                />
                <Divider />
              </>
            )}
            {experiences.map((item, index) => {
              // Check if there are any gaps after this experience
              const hasGapAfter = gaps.some((gap) => {
                const expIndex = experiences.findIndex((exp) => {
                  if (!exp.startDate) return false
                  const expStart = dayjs.utc(exp.startDate)
                  const gapStart = dayjs.utc(gap.startDate)
                  return (
                    expStart.isValid() &&
                    gapStart.isValid() &&
                    expStart.isSame(gapStart, 'month')
                  )
                })
                return expIndex === index
              })

              const isLastExperience = index === experiences.length - 1
              const shouldShowDivider =
                !isLastExperience || hasGapAfter || endGap

              return (
                <div key={index}>
                  <Box
                    className="flex flex-row items-center"
                    onClick={() => {
                      setCurrentIndex(index)
                      openEmploymentDetailsDrawer(
                        item.employmentExperienceId,
                        applicationId
                      )
                    }}
                  >
                    {item.isUnemployment ? (
                      <UnemploymentSummaryInfo item={item} />
                    ) : (
                      <EmploymentSummaryInfo item={item} onOpenVoe={openVoe} />
                    )}
                  </Box>
                  {/* Display gaps using the gap calculation logic */}
                  {gaps.map((gap, gapIndex) => {
                    const shouldDisplayGap =
                      experiences.findIndex((exp) => {
                        if (!exp.startDate) return false
                        const expStart = dayjs.utc(exp.startDate)
                        const gapStart = dayjs.utc(gap.startDate)
                        return (
                          expStart.isValid() &&
                          gapStart.isValid() &&
                          expStart.isSame(gapStart, 'month')
                        )
                      }) === index

                    return shouldDisplayGap ? (
                      <div key={`gap-${gapIndex}`}>
                        <Divider />
                        <EmploymentGapRow
                          startDate={gap.displayStartDate}
                          endDate={gap.displayEndDate}
                          duration={gap.duration}
                          durationString={gap.durationString}
                          onAddEmployment={onAddEmployment}
                          onAddUnemployment={onAddUnemployment}
                        />
                      </div>
                    ) : null
                  })}
                  {shouldShowDivider && <Divider />}
                </div>
              )
            })}
            {endGap && (
              <EmploymentGapRow
                startDate={endGapStartDate}
                endDate={endGapEndDate}
                duration={endGapDuration}
                durationString={endGapDurationString}
                onAddEmployment={onAddEmployment}
                onAddUnemployment={onAddUnemployment}
              />
            )}
          </>
        ) : (
          <StyledAlert
            title="No jobs reported"
            description="Add an employment experience to get started"
          />
        )}
      </div>
    </div>
  )
}

export default EmploymentTable
